import { EditableText, defineBlock } from "eddev/blocks"
import { Col, Container, Grid } from "@components/layout/Grid"
import { cn } from "@utils/tw"
import { ComponentPropsWithoutRef, forwardRef } from "react"
import { VSpace } from "@components/layout/Spacer"
import { Heading } from "@components/atoms/Heading"
import { Link } from "eddev/routing"
import {
  EMAIL,
  FACEBOOK,
  LINKEDIN,
  MOTORING_ALT,
  PHONE,
  X,
  YOUTUBE,
  COPIED,
  COPY,
  LINKARROW,
} from "@components/atoms/Icons"
import { useContactDetails, useForm, useSocialLinks } from "@hooks/queries"
import { useHydrating } from "eddev/routing"
import { dynamic } from "eddev/dynamic"
import { useEffect, useRef, useState } from "react"

export const meta: BlockMeta = {
  title: "Contact Panel",
  category: "common",
  icon: "admin-links",
  keywords: ["quick links"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

const GravityForm = dynamic(() => import("@components/forms/organisms/GravityForm"), "GravityForm")

export default defineBlock("general/contact-panel", (props) => {
  const social = useSocialLinks(undefined).data?.siteSettings?.themeSiteSettings?.socialMedia
  const detail = useContactDetails(undefined).data?.siteSettings?.themeSiteSettings?.contactDetails
  const form = useForm({ id: "1" })
  const hydrating = useHydrating()
  const phoneInput = useRef<HTMLInputElement>(null)
  const emailInput = useRef<HTMLInputElement>(null)
  const officeInput = useRef<HTMLInputElement>(null)
  const poBoxInput = useRef<HTMLInputElement>(null)
  const mediaEnquiriesInput = useRef<HTMLInputElement>(null)
  const internationalEnquiriesInput = useRef<HTMLInputElement>(null)

  const [copied, setCopied] = useState<string | null>(null)
  const [copiedLink, setCopiedLink] = useState<boolean>(false)

  useEffect(() => {
    if (copiedLink) {
      const timeout = setTimeout(() => {
        setCopied(null)
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [copied])

  async function copyToClipboard(text: string) {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      setCopied(text)
      setCopiedLink(true)
    } else return
  }

  return (
    <ContactContainer>
      <Grid>
        <Col initial={12}>
          <EditableText store="title" as="h2" defaultValue="Get in touch" />
          <VSpace initial={7} />
        </Col>
        {!hydrating && detail && social && (
          <>
            <Col initial={12} tablet={6}>
              <Details>
                {detail && (
                  <>
                    {(detail.phone || detail.email) && (
                      <ul>
                        {detail.phone && (
                          <li>
                            <CopyLabel onClick={() => copyToClipboard(detail.phone!)}>
                              <input
                                className="sr-only"
                                type="text"
                                value={detail.phone}
                                ref={phoneInput}
                                hidden
                                aria-hidden
                                readOnly
                              />
                              <span className="label">{PHONE} Phone</span>
                              {detail.phone === copied ? (
                                <i>
                                  <span className="action-text">Copied</span> {COPIED}
                                </i>
                              ) : (
                                <i>
                                  <span className="detail leading-4">{detail.phone}</span>
                                  {COPY}
                                </i>
                              )}
                            </CopyLabel>
                          </li>
                        )}
                        {detail.email && (
                          <li>
                            <CopyLabel onClick={() => copyToClipboard(detail.email!)}>
                              <input
                                className="sr-only"
                                type="text"
                                value={detail.email}
                                ref={emailInput}
                                hidden
                                aria-hidden
                                readOnly
                              />
                              <span className="label">{EMAIL} Email</span>
                              {detail.email === copied ? (
                                <i>Copied {COPIED}</i>
                              ) : (
                                <i>
                                  <span className="detail leading-4">{detail.email}</span>
                                  {COPY}
                                </i>
                              )}
                            </CopyLabel>
                          </li>
                        )}
                      </ul>
                    )}

                    {(detail.office || detail.poBox) && (
                      <ul>
                        {detail.office && (
                          <li>
                            <CopyLabel onClick={() => copyToClipboard(detail.office!)}>
                              <input
                                className="sr-only"
                                type="text"
                                value={detail.office}
                                ref={officeInput}
                                hidden
                                aria-hidden
                                readOnly
                              />
                              <span className="label">{MOTORING_ALT} Office</span>
                              {detail.office === copied ? (
                                <i>Copied {COPIED}</i>
                              ) : (
                                <i>
                                  <span className="detail leading-4">{detail.office}</span>
                                  {COPY}
                                </i>
                              )}
                            </CopyLabel>
                          </li>
                        )}
                        {detail.poBox && (
                          <li>
                            <CopyLabel onClick={() => copyToClipboard(detail.poBox!)}>
                              <input
                                className="sr-only"
                                type="text"
                                value={detail.poBox}
                                ref={poBoxInput}
                                hidden
                                aria-hidden
                                readOnly
                              />
                              <span className="label">{MOTORING_ALT} PO Box</span>
                              {detail.poBox === copied ? (
                                <i>Copied {COPIED}</i>
                              ) : (
                                <i>
                                  <span className="detail leading-4">{detail.poBox}</span>
                                  {COPY}
                                </i>
                              )}
                            </CopyLabel>
                          </li>
                        )}
                      </ul>
                    )}

                    {(detail.mediaEnquiries || detail.internationalEnquiries) && (
                      <ul>
                        {detail.mediaEnquiries && (
                          <li>
                            <CopyLabel onClick={() => copyToClipboard(detail.mediaEnquiries!)}>
                              <input
                                className="sr-only"
                                type="text"
                                value={detail.mediaEnquiries}
                                ref={mediaEnquiriesInput}
                                hidden
                                aria-hidden
                                readOnly
                              />
                              <span className="label">{EMAIL} Media enquiries</span>
                              {detail.mediaEnquiries === copied ? (
                                <i>Copied {COPIED}</i>
                              ) : (
                                <i>
                                  <span className="detail leading-4">{detail.mediaEnquiries}</span>
                                  {COPY}
                                </i>
                              )}
                            </CopyLabel>
                          </li>
                        )}
                        {detail.internationalEnquiries && (
                          <li>
                            <CopyLabel onClick={() => copyToClipboard(detail.internationalEnquiries!)}>
                              <input
                                className="sr-only"
                                type="text"
                                value={detail.internationalEnquiries}
                                ref={internationalEnquiriesInput}
                                hidden
                                aria-hidden
                                readOnly
                              />
                              <span className="label">{EMAIL} International enquiries</span>
                              {detail.internationalEnquiries === copied ? (
                                <i>Copied {COPIED}</i>
                              ) : (
                                <i>
                                  <span className="detail leading-4">{detail.internationalEnquiries}</span> {COPY}
                                </i>
                              )}
                            </CopyLabel>
                          </li>
                        )}
                      </ul>
                    )}
                  </>
                )}
                {social && (
                  <>
                    <ul>
                      {social.x && (
                        <li>
                          <LinkWithIcon href={social.x} target="_blank">
                            <span className="label">{X} X (formerly Twitter)</span>
                            <i>
                              <span className="detail leading-4">@aaacomms</span>
                              {LINKARROW}
                            </i>
                          </LinkWithIcon>
                        </li>
                      )}
                      {social.facebook && (
                        <li>
                          <LinkWithIcon href={social.facebook} target="_blank">
                            <span className="label">{FACEBOOK} Facebook</span>
                            <i>
                              <span className="detail leading-4">AusAutoAssociation</span> {LINKARROW}
                            </i>
                          </LinkWithIcon>
                        </li>
                      )}
                      {social.linkedin && (
                        <li>
                          <LinkWithIcon href={social.linkedin} target="_blank">
                            <span className="label">{LINKEDIN} LinkedIn</span>
                            <i>
                              <span className="detail leading-4">AAA</span> {LINKARROW}
                            </i>
                          </LinkWithIcon>
                        </li>
                      )}
                      {social.youtube && (
                        <li>
                          <LinkWithIcon href={social.youtube} target="_blank">
                            <span className="label">{YOUTUBE} YouTube</span>
                            <i>
                              <span className="detail leading-4">@aaacomms</span> {LINKARROW}
                            </i>
                          </LinkWithIcon>
                        </li>
                      )}
                    </ul>
                  </>
                )}
              </Details>
            </Col>
            <Col initial={12} tablet={6}>
              <FormContainer>
                {form.data?.gravityForm?.form && (
                  <>
                    <Heading as="h4" variant="h4">
                      {form.data.gravityForm.form.title}
                    </Heading>
                    <VSpace initial={5} />
                    <GravityForm form={form.data.gravityForm.form} />
                  </>
                )}
              </FormContainer>
            </Col>
          </>
        )}
      </Grid>
    </ContactContainer>
  )
})

const ContactContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => (
    <Container
      ref={ref}
      className={cn("my-6", "[&_h2]:type-title-l [&_h2]:my-0 [&_h2]:w-full [&_h2]:text-center [&_h2]:block", className)}
      {...props}
    />
  ),
)
ContactContainer.displayName = "ContactContainer"

const Details = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col p-4 mb-6",
      "[&_ul]:flex [&_ul]:flex-col [&_ul]:mb-5 [&_ul]:gap-5 [&_ul]:px-0 [&_ul]:pb-5 [&_ul]:border-b [&_ul]:border-light-grey",
      "[&_ul:last-of-type]:mb-0 [&_ul:last-of-type]:pb-0 [&_ul:last-of-type]:border-b-0",
      "[&_li]:flex [&_li]:items-center [&_li]:justify-between",
      "[&_li>a]:w-full [&_li>a]:no-underline [&_li>a]:text-black [&_li>a]:flex [&_li>a]:flex-col",
      "[&_li>button]:w-full [&_li>button]:no-underline [&_li>button]:text-black [&_li>button]:flex [&_li>button]:flex-col",
      "md:[&_li>a]:flex-row md:[&_li>a]:items-center md:[&_li>a]:justify-between",
      "md:[&_li>button]:flex-row md:[&_li>button]:items-center md:[&_li>button]:justify-between",
      "md:[&_li>a:hover_i]:underline md:[&_li>button:hover_i]:underline",
      "[&_.label]:w-full [&_.label]:type-title-xs [&_.label]:flex [&_.label]:items-center [&_.label]:gap-3 [&_.label]:mb-2",
      "md:[&_.label]:gap-4 md:[&_.label]:mb-0",
      "[&_.detail]:inline-block [&_.detail]:max-w-[150px]",
      "md:[&_.detail]:max-w-none",
      "[&_li_i]:not-italic [&_li_i]:type-label-caps [&_li_i]:text-dark-grey",
      className,
    )}
    {...props}
  />
))
Details.displayName = "Details"

const FormContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-yellow p-7 rounded-md h-full flex flex-col",
      "[&_main>div:last-of-type]:absolute [&_main>div:last-of-type]:bottom-0 [&_main>div:last-of-type]:translate-y-[82px] [&_main>div:last-of-type]:max-w-[60%]",
      "md:[&_main>div:last-of-type]:translate-y-[75px]",
      "lg:[&_main>div:last-of-type]:translate-y-[105px]",
      "[&_main_label]:text-dark-grey",
      "[&_footer]:mt-6 [&_footer]:pt-6 [&_footer]:border-t [&_footer]:border-black",
      "[&_footer_button.button]:!bg-black [&_footer_button.button]:!text-white [&_footer_button.button]:float-right",
      className,
    )}
    {...props}
  />
))
FormContainer.displayName = "FormContainer"

const CopyLabel = forwardRef<HTMLLabelElement, ComponentPropsWithoutRef<"label">>(({ className, ...props }, ref) => (
  <label
    ref={ref}
    className={cn(
      "relative flex flex-col md:flex-row md:items-center md:justify-between",
      "w-full no-underline text-black cursor-pointer",
      "[&_svg]:pointer-events-none",
      "[&_i]:inline-flex [&_i]:w-full [&_i]:justify-between [&_i]:items-center [&_i]:gap-2 [&_i]:cursor-pointer [&_i]:pl-[calc(var(--spacing-3)+24px)]",
      "md:[&_i]:justify-end",
      "[&:hover_i]:!text-black [&:hover_i]:!fill-black",
      "appearance-none [-webkit-tap-highlight-color:transparent]",
      "focus:outline-none",
      className,
    )}
    {...props}
  />
))
CopyLabel.displayName = "CopyLabel"

const LinkWithIcon = forwardRef<HTMLAnchorElement, ComponentPropsWithoutRef<typeof Link>>(
  ({ className, ...props }, ref) => (
    <Link
      ref={ref}
      className={cn(
        "relative flex flex-col md:flex-row md:items-center md:justify-between",
        "w-full no-underline text-black cursor-pointer",
        "[&_svg]:pointer-events-none md:[&_svg]:hidden",
        "[&_i]:inline-flex [&_i]:w-full [&_i]:justify-between [&_i]:items-center [&_i]:gap-2 [&_i]:cursor-pointer [&_i]:pl-[calc(var(--spacing-3)+24px)]",
        "md:[&_i]:pl-0 md:[&_i]:justify-end",
        "[&:hover_i]:!text-black [&:hover_i]:!fill-black",
        "appearance-none [-webkit-tap-highlight-color:transparent]",
        "focus:outline-none",
        className,
      )}
      {...props}
    />
  ),
)
LinkWithIcon.displayName = "LinkWithIcon"
